import { type RefObject, useCallback, useRef, useState } from 'react';

import type { SpeechActivityUpdate } from '../videoToolboxActivityLogLib';
import {
  type BrowserSpeechRecognitionInstance,
  getBrowserSpeechRecognitionConstructor,
  mergeSpeechDisplayText,
  parseSpeechRecognitionResultEvent,
  shouldRestartSpeechRecognition,
} from './browserSpeechRecognitionLib';

export type { SpeechActivityUpdate };

type UseBrowserSpeechRecognitionOptions = {
  isRecordingRef: RefObject<boolean>;
  onSpeechActivityChange?: (update: SpeechActivityUpdate) => void;
};

/**
 * Browser SpeechRecognition for live draft narration transcripts.
 * Restarts automatically while recording — Chrome ends continuous sessions quickly.
 */
export const useBrowserSpeechRecognition = ({
  isRecordingRef,
  onSpeechActivityChange,
}: UseBrowserSpeechRecognitionOptions) => {
  const recognitionRef = useRef<BrowserSpeechRecognitionInstance | undefined>(undefined);
  const finalTextRef = useRef('');
  const interimTextRef = useRef('');
  const permissionDeniedRef = useRef(false);
  const onSpeechActivityChangeRef = useRef(onSpeechActivityChange);
  onSpeechActivityChangeRef.current = onSpeechActivityChange;
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string>();
  const isSupported = getBrowserSpeechRecognitionConstructor() != null;

  const publishSpeechActivity = useCallback((nextFinalText: string, nextInterimText: string) => {
    finalTextRef.current = nextFinalText;
    interimTextRef.current = nextInterimText;
    setFinalText(nextFinalText);
    setInterimText(nextInterimText);
  }, []);

  const publishUtterance = useCallback((utterance: string) => {
    onSpeechActivityChangeRef.current?.({ utterance });
  }, []);

  const publishInterim = useCallback((interim: string) => {
    onSpeechActivityChangeRef.current?.({ interim });
  }, []);

  const restartRecognition = useCallback((recognition: BrowserSpeechRecognitionInstance) => {
    if (permissionDeniedRef.current || !isRecordingRef.current || recognitionRef.current !== recognition) return;
    try {
      recognition.start();
    } catch {
      // start() throws if called while already active — safe to ignore while recording.
    }
  }, [isRecordingRef]);

  const attachRecognitionHandlers = useCallback((recognition: BrowserSpeechRecognitionInstance) => {
    recognition.onresult = event => {
      const parsed = parseSpeechRecognitionResultEvent(event, finalTextRef.current);
      publishSpeechActivity(parsed.finalText, parsed.interimText);
      for (const utterance of parsed.newFinalUtterances) {
        publishUtterance(utterance);
      }
      publishInterim(parsed.interimText);
    };
    recognition.onerror = speechEvent => {
      if (speechEvent.error === 'not-allowed') {
        permissionDeniedRef.current = true;
        recognition.onend = null;
        setError('Speech recognition permission denied.');
        return;
      }
      if (shouldRestartSpeechRecognition(speechEvent.error, isRecordingRef.current ?? false)) {
        restartRecognition(recognition);
        return;
      }
      if (speechEvent.error !== 'no-speech' && speechEvent.error !== 'aborted') {
        setError('Speech recognition unavailable.');
      }
    };
    recognition.onend = () => {
      if (permissionDeniedRef.current || !isRecordingRef.current || recognitionRef.current !== recognition) return;
      restartRecognition(recognition);
    };
  }, [isRecordingRef, publishInterim, publishSpeechActivity, publishUtterance, restartRecognition]);

  const stopRecognition = useCallback((): string => {
    const recognition = recognitionRef.current;
    recognitionRef.current = undefined;
    if (recognition) {
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // ignore stop errors
      }
    }
    const captured = mergeSpeechDisplayText(finalTextRef.current, interimTextRef.current);
    const pendingInterim = interimTextRef.current.trim();
    if (pendingInterim) {
      publishUtterance(pendingInterim);
    }
    publishSpeechActivity(finalTextRef.current, '');
    publishInterim('');
    return captured;
  }, [publishInterim, publishSpeechActivity, publishUtterance]);

  const startRecognitionSession = useCallback(() => {
    const SpeechRecognitionClass = getBrowserSpeechRecognitionConstructor();
    if (!SpeechRecognitionClass) return false;

    permissionDeniedRef.current = false;

    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      try {
        recognitionRef.current.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = undefined;
    }

    publishSpeechActivity('', '');
    publishInterim('');
    setError(undefined);

    try {
      const recognition = new SpeechRecognitionClass();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      attachRecognitionHandlers(recognition);
      recognition.start();
      recognitionRef.current = recognition;
      return true;
    } catch {
      setError('Speech recognition unavailable.');
      return false;
    }
  }, [attachRecognitionHandlers, publishInterim, publishSpeechActivity]);

  const speechText = mergeSpeechDisplayText(finalText, interimText);

  const startRecognition = useCallback(
    () => startRecognitionSession(),
    [startRecognitionSession],
  );

  return {
    startRecognition,
    stopRecognition,
    speechText,
    isSupported,
    error,
  };
};
